import axios from 'axios'
import { Message } from 'element-ui'
// 创建缓存Map
const cacheMap = new Map()
// 扩展localStorage缓存
const storageCache = {
  /**
   * 设置缓存
   * @param {string} key 缓存key
   * @param {any} data 缓存数据
   * @param {number} expire 过期时间(毫秒)
   */
  set(key, data, expire) {
    localStorage.setItem(key, JSON.stringify({
      expire: Date.now() + expire,
      data
    }))
  },
  
  /**
   * 获取缓存
   * @param {string} key 缓存key
   * @returns {any|null} 缓存数据或null
   */
  get(key) {
    const item = localStorage.getItem(key)
    if (!item) return null
    
    try {
      const parsed = JSON.parse(item)
      if (parsed.expire > Date.now()) {
        return parsed.data
      }
      localStorage.removeItem(key) // 清除过期缓存
      return null
    } catch (e) {
      return null
    }
  },
  
  remove(key) {
    localStorage.removeItem(key)
  },
  
  clear() {
    localStorage.clear()
  }
}

// 修改requestWithCache函数
const requestWithCache = (config, cacheTime = 300000) => {
  const cacheKey = generateCacheKey(config)
  
  // 尝试从内存缓存获取
  if (cacheMap.has(cacheKey)) {
    const { expire, data } = cacheMap.get(cacheKey)
    if (Date.now() < expire) {
      return Promise.resolve(JSON.parse(JSON.stringify(data)))
    }
    cacheMap.delete(cacheKey)
  }
  
  // 尝试从本地存储获取
  const storageData = storageCache.get(cacheKey)
  if (storageData) {
    cacheMap.set(cacheKey, { // 存入内存缓存
      expire: Date.now() + cacheTime,
      data: storageData
    })
    return Promise.resolve(JSON.parse(JSON.stringify(storageData)))
  }
  
  // 发起真实请求
  return axios(config).then(response => {
    const resData = response.data
    
    if (config.method?.toLowerCase() === 'get' && (!resData.code || resData.code === 200)) {
      cacheMap.set(cacheKey, {
        expire: Date.now() + cacheTime,
        data: resData
      })
      
      // 同时存入本地存储（重要数据）
      if (config.usePersistentCache) {
        storageCache.set(cacheKey, resData, cacheTime)
      }
    }
    
    return resData
  })
}

/**
 * 生成缓存key
 * @param {object} config 请求配置
 * @returns {string} 缓存key
 */
const generateCacheKey = (config) => {
  return `${config.method}:${config.url}:${JSON.stringify(config.params)}:${JSON.stringify(config.data)}`
}

/**
 * 带缓存的请求
 * @param {object} config 请求配置
 * @param {number} cacheTime 缓存时间(毫秒)，默认5分钟
 * @returns {Promise} 
 */
// const requestWithCache = (config, cacheTime = 300000) => {
//   const cacheKey = generateCacheKey(config)
  
//   // 检查缓存是否存在且未过期
//   if (cacheMap.has(cacheKey)) {
//     const { expire, data } = cacheMap.get(cacheKey)
//     if (Date.now() < expire) {
//       //console.log('[缓存命中]', cacheKey)
//       return Promise.resolve(JSON.parse(JSON.stringify(data))) // 返回深拷贝
//     }
//     cacheMap.delete(cacheKey) // 清除过期缓存
//   }
  
//   // 发起真实请求
//   return axios(config).then(response => {
//     const resData = response.data
    
//     // 只有GET请求且返回成功才缓存
//     if (config.method?.toLowerCase() === 'get' && (!resData.code || resData.code === 200)) {
//       cacheMap.set(cacheKey, {
//         expire: Date.now() + cacheTime,
//         data: resData
//       })
//       //console.log('[设置缓存]', cacheKey)
//     }
    
//     return resData
//   })
// }



// 创建axios实例
const service = axios.create({
  baseURL: process.env.VUE_APP_BASE_API, // 从环境变量读取API基础地址
  timeout: 10000 // 请求超时时间
})


service.request = (config) => {
    // 默认缓存配置
    const defaultCacheConfig = {
      cache: false,
      cacheTime: 300000, // 5分钟
      usePersistentCache: false,
      forceRefresh: false // 强制刷新缓存
    }
    
    // 合并配置
    const cacheConfig = { ...defaultCacheConfig, ...config }
    
    // 强制刷新时清除缓存
    if (cacheConfig.forceRefresh) {
      const cacheKey = generateCacheKey(config)
      cacheMap.delete(cacheKey)
      storageCache.remove(cacheKey)
    }
    
    // 检查是否需要缓存
    if (cacheConfig.cache) {
      return requestWithCache(config, cacheConfig.cacheTime)
    }
  return axios(config)
}

// // 防止XSS攻击
// service.interceptors.request.use(config => {
//   if (config.params) {
//     Object.keys(config.params).forEach(key => {
//       if (typeof config.params[key] === 'string') {
//         config.params[key] = config.params[key].replace(/</g, '&lt;')
//       }
//     })
//   }
//   return config
// })

//请求拦截器
service.interceptors.request.use(
  config => {
    removePendingRequest(config)
    addPendingRequest(config)
    if (config.params) {
      // Object.keys(config.params).forEach(key => {
      //   if (typeof config.params[key] === 'string') {
      //     config.params[key] = config.params[key].replace(/</g, '&lt;')
      //   }
      // })
      
      config.paramsSerializer = params => {
        return Object.keys(params)
          .map(key => {
            const value = params[key]
            if (Array.isArray(value)) {
              return value.map(v => `${encodeURIComponent(key)}=${encodeURIComponent(v)}`).join('&')
            }
            return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
          })
          .join('&')
      }
    }
    // 可在此处添加公共请求参数
    config.params = {
      ...config.params,
      _t: Date.now() // 防止缓存
    }
    return config
  },
  error => {
    //console.log('请求错误:', error)
    return Promise.reject(error)
  }
)

// 响应拦截器
service.interceptors.response.use(
  response => {
    removePendingRequest(response.config)
    const res = response.data
    
    // 根据后端接口结构调整
    if (res.code && res.code !== 200) {
      Message({
        message: res.message || '请求失败',
        type: 'error',
        duration: 3 * 1000
      })
      return Promise.reject(new Error(res.message || 'Error'))
    }
    return res.data || res // 兼容不同数据结构
  },
  error => {
    //console.log('响应错误:', error)
    
    removePendingRequest(error.config)
    // 处理HTTP错误状态码
    if (error.response) {
      switch (error.response.status) {
        case 400:
          error.message = '请求参数错误'
          break
        case 404:
          error.message = '请求资源不存在'
          break
        case 500:
          error.message = '服务器内部错误'
          break
        default:
          error.message = `连接错误 ${error.response.status}`
      }
    } else {
      error.message = '连接到服务器失败'
    }
    
    Message({
      message: error.message,
      type: 'error',
      duration: 3 * 1000
    })
    
    return Promise.reject(error)
  }
)


// 在 request.js 中添加
const MAX_RETRY_COUNT = 3
const RETRY_DELAY = 1000

service.interceptors.response.use(undefined, error => {
  const config = error.config
  
  if (!config || !config.retry) {
    return Promise.reject(error)
  }
  
  config.retryCount = config.retryCount || 0
  
  if (config.retryCount >= MAX_RETRY_COUNT) {
    return Promise.reject(error)
  }
  
  config.retryCount += 1
  
  return new Promise(resolve => {
    setTimeout(() => {
      resolve(service(config))
    }, RETRY_DELAY)
  })
})
const pendingRequests = new Map()

const addPendingRequest = (config) => {
  const requestKey = `${config.method}::${config.url}::${JSON.stringify(config.params)}::${JSON.stringify(config.data)}`
  config.cancelToken = config.cancelToken || new axios.CancelToken(cancel => {
    if (!pendingRequests.has(requestKey)) {
      pendingRequests.set(requestKey, cancel)
    }
  })
}

const removePendingRequest = (config) => {
  const requestKey = `${config.method}::${config.url}::${JSON.stringify(config.params)}::${JSON.stringify(config.data)}`
  if (pendingRequests.has(requestKey)) {
    const cancel = pendingRequests.get(requestKey)
    cancel(requestKey)
    pendingRequests.delete(requestKey)
  }
}



// // 导出取消所有请求的方法
// export function cancelAllRequests() {
//   pendingRequests.forEach(cancel => {
//     cancel('取消所有未完成请求')
//   })
//   pendingRequests.clear()
// }


// 添加缓存清除方法
service.clearCache = (key) => {
  if (key) {
    cacheMap.delete(key)
  } else {
    cacheMap.clear()
  }
}


export default service